
import { Component, Emit, Mixins, Prop, Ref, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { namespace } from 'vuex-class';

import type { SnowplowContext, SnowplowModuleContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import { type PopularTitleGraphqlFragment } from '@/pages/graphql/fragments/PopularTitle.fragment';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { UpcomingTitleGraphqlFragment } from '@/pages/graphql/fragments/UpcomingTitle.fragment';

import InfiniteBiScroll from '@/components/list/InfiniteBiScroll.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import GridVirtualScroller from '@/components/virtual-scroller/GridVirtualScroller.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';

import { Offer, SraEventAction, TitleListType } from '@/@types/graphql-types';
import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';
import { CollectionType } from '@/enums/collection-type';
import { ImpressionTrackingEvents } from '@/enums/events';
import { btoa } from '@/helpers/base64-helper';
import type { TitleEventPayloadGraphql } from '@/helpers/title-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import { FilterCollection } from '@/stores/filter.store';

const Language = namespace('language');
const Routing = namespace('routing');
const User = namespace('user');
const Filter = namespace('filter');

type Title = { node: PopularTitleGraphqlFragment | UpcomingTitleGraphqlFragment };

@Component({
	name: 'TitleListGridGraphql',
	components: {
		GridVirtualScroller,
		PosterGraphql,
		InfiniteScroll,
		InfiniteBiScroll,
		TitleDetailLink,
	},
})
export default class TitleListGridGraphql extends Mixins(BackendTrackingMixin) {
	@Prop() hasNoTitles: boolean;
	@Prop() isAtEndOfList: boolean;
	@Prop() isFirstPage: boolean;
	@Prop() loading: boolean;
	@Prop() titles: Title[];
	@Prop({ default: 0 }) titlesAmount: number;
	@Prop({ default: 100 }) titlesPerPage: number;
	@Prop() collectionType: CollectionType;
	@Prop({ default: () => [] }) additionalContexts: (SnowplowModuleContext | SnowplowContext)[];
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragment;
	@Prop({ default: true }) virtualScrolling: boolean;
	@Prop({ default: false }) infiniteBiScrollEnabled: boolean;

	@Ref('infiniteScroll') readonly infiniteScroll?: InfiniteScroll;
	@Ref('gridVirtualScroller') gridVirtualScroller?:
		| GridVirtualScroller<PopularTitleGraphqlFragment>
		| GridVirtualScroller<UpcomingTitleGraphqlFragment>;

	@Language.Getter declare country: string;
	@Language.Getter declare language: string;

	@Routing.State activeRoute: Route;

	@User.Getter isLoggedIn: (providerName?: string) => boolean;

	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	isMounted = false;
	gridHasRows = false;
	TitleListType = TitleListType;

	get showFreeTag() {
		return (
			['app.titles.popular.list', 'app.titles.popular.movie', 'app.titles.popular.show'].includes(
				this.activeRoute?.name as string
			) && !this.isLoggedIn()
		);
	}

	@Watch('titles')
	onTitlesChange() {
		this.gridVirtualScroller?.updateRows();
	}

	@Emit('onPosterClick')
	onPosterClick(title: Title, index: number): TitleEventPayloadGraphql {
		if (this.sponsoredAd?.campaign?.node.nodeId === title.node.id) {
			TrackingHelper.trackEvent(
				ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
				{
					action: 'click',
					label: `${title.node.id}_${this.sponsoredRecommendationPackageId}`,
					property: 'poster',
				},
				this.additionalContexts
			);
		}
		const hasFreeBadge = this.showFreeTag && (title.node as PopularTitleGraphqlFragment)?.freeOffersCount > 0;
		const label = hasFreeBadge ? 'free' : '';
		return { title: title.node as TitleEventPayloadGraphql['title'], index, label };
	}

	get disableInfiniteScroll() {
		const isTitleDetailPage = this.activeRoute.name?.includes('app.detail');
		return !this.gridHasRows || this.hasNoTitles || this.isAtEndOfList || this.loading || isTitleDetailPage;
	}

	// CLS issue: upon filtering, the posters may change their position e.g. from 1st to 3rd.
	// this change of order, together with a not-perfect mobile connection that will take more than 500ms for the filtering request, will be picked up as CLS: the existing element changes its position.
	// to circumvent this, we give the posters a :key that is sort of bound to the request, so we can re-create the posters instead of being reused and moved through the DOM.
	get requestHash() {
		const filterEncoded = btoa(JSON.stringify(this.filters(this.collectionType)));
		return filterEncoded;
	}

	get shouldShowQuickActions() {
		return this.isLoggedIn();
	}

	get shouldRender() {
		return BUILD_CONFIG.globals.BUILD_TARGET === 'SPA' || this.isMounted;
	}

	get sponsoredRecommendationPackageId() {
		return this.sponsoredAd?.campaign.watchNowOffer.package.packageId;
	}

	get sponsoredRecommendationCohort() {
		return this.sponsoredAd?.holdoutGroup ? '0' : '1';
	}

	mounted() {
		this.isMounted = true;
	}

	impressionCallback(title: Title) {
		if (this.sponsoredAd?.campaign?.node.nodeId === title.node?.id) {
			return this.$backendTracking.trackSraEvent(SraEventAction.Impression, this.sponsoredAd);
		}
		return;
	}

	posterGraphqlProps(title: Title, index: number): PosterGraphqlProps {
		let impressionTrackingOptions = {};
		const titleId = title.node.id;
		if (this.sponsoredAd?.campaign?.node.nodeId === titleId) {
			impressionTrackingOptions = {
				impressionTrackingActive: true,
				impressionTrackingCategory: ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
				impressionTrackingLabel: `${titleId}_${this.sponsoredRecommendationPackageId}`,
				impressionTrackingProperty: 'poster',
				impressionTrackingValue: this.sponsoredRecommendationCohort,
				impressionTrackingObservableOptions: mrcObservableOptions,
			};
		}

		const props: PosterGraphqlProps = {
			titleId: this.getTitleId(title),
			titleObjectId: title.node.objectId,
			titleObjectType: title.node.objectType,
			titleName: title.node.content.title,
			titleScoring: title.node.content.scoring,
			titleSeasonNumber: this.getTitleSeasonNumber(title),
			isTvShowTracked: this.getIsTvShowTracked(title),
			isInCustomList: this.getIsInCustomList(title),
			isInWatchlist: this.isTitleInList(TitleListType.WatchNext, title.node),
			isInSeenList: this.isTitleInList(TitleListType.CaughtUp, title.node),
			isInLikeList: this.isTitleInList(TitleListType.Likelist, title.node),
			isInDislikeList: this.isTitleInList(TitleListType.Dislikelist, title.node),
			isTitleReleased: title.node.content.isReleased,
			forceQuickActions: this.isFirstPage,
			showQuickActions: this.shouldShowQuickActions && this.shouldRender,
			showRibbon: !this.shouldShowQuickActions,
			watchNowOffer: (this.getWatchNowOffer(title) as Offer) ?? undefined,
			lazy: BUILD_CONFIG.globals.BUILD_TARGET === 'SSR' ? index >= global.IMG_POSTER_LOAD_COUNT : true,
			freeOffersCount: (title.node as PopularTitleGraphqlFragment)?.freeOffersCount ?? 0,
			showFreeTag: this.showFreeTag,
			...impressionTrackingOptions,
		};

		if (title.node.__typename === 'Show') {
			props.titleBackdrops = title.node.content.backdrops;
			props.seenEpisodeCount = (title.node.seenState || {}).seenEpisodeCount;
		}

		if (title.node.content.posterUrl) {
			props.titlePosterUrl = title.node.content.posterUrl;
		}

		return props;
	}

	getTitleSeasonNumber(title: Title) {
		if (title.node.__typename === 'Season') return title.node.content.seasonNumber;
		return undefined;
	}

	getIsInCustomList(title: Title) {
		if (title.node.__typename === 'Movie' || title.node.__typename === 'Show')
			return !!title.node.customlistEntries?.length;
		else if (title.node.__typename === 'Season') return !!title.node.show.customlistEntries?.length;
		return false;
	}
	getIsTvShowTracked(title: Title) {
		if (title.node.__typename === 'Show') return title.node.tvShowTrackingEntry?.createdAt;
		else if (title.node.__typename === 'Season') return title.node.show.tvShowTrackingEntry;
		return false;
	}
	getTitleId(title: Title) {
		if (title.node.__typename === 'Season') return title.node.show.id;
		return title.node.id;
	}
	getTitle(title: Title) {
		if (title.node.__typename === 'Season') return title.node.show;
		return title.node;
	}

	getWatchNowOffer(title: Title) {
		if (title.node.__typename === 'Show' || title.node.__typename === 'Movie') return title.node.watchNowOffer;
		else if (title.node.__typename === 'Season') return title.node.show.watchNowOffer;
		return undefined;
	}

	isTitleInList(listName: TitleListType, title: PopularTitleGraphqlFragment | UpcomingTitleGraphqlFragment) {
		switch (listName) {
			case TitleListType.WatchNext:
				if (title.__typename === 'Movie' || title.__typename === 'Show')
					return !!title?.watchlistEntryV2?.createdAt;
				else if (title.__typename === 'Season') return title.show.watchlistEntryV2?.createdAt;
			case TitleListType.Likelist:
				if (title.__typename === 'Movie' || title.__typename === 'Show')
					return !!title?.likelistEntry?.createdAt;
				else if (title.__typename === 'Season') return title.show.likelistEntry?.createdAt;
			case TitleListType.Dislikelist:
				if (title.__typename === 'Movie' || title.__typename === 'Show')
					return !!title?.dislikelistEntry?.createdAt;
				else if (title.__typename === 'Season') return title.show.dislikelistEntry?.createdAt;
			case TitleListType.CaughtUp:
				if (title.__typename === 'Movie') return !!title?.seenlistEntry?.createdAt;
				if (title.__typename === 'Show') return title?.seenState?.progress === 100;
				if (title.__typename === 'Season') return title.show.seenState?.progress === 100;
				break;
			default:
				return false;
		}
	}

	// Resets the internal state of the infinite scroll. Needs to be called at each filter change.
	resetInfiniteScroll() {
		this.infiniteScroll?.reset();
		this.gridVirtualScroller?.resetRows();
	}
}
